/**
 * Dynamic Lists Module
 * @uses window.userVars
 * @uses window.dynamicListsFunctions
 * @uses window.trialData
 * @return {object} module
 */
function initDynamicListsModule() {
    return {
        initFunctions,
        expandTrial,
        trialGeneratorListParallel,
        trialGeneratorListNested,
        getDynamicValue,
        getDynamicListValues,
        getStaticListValues,
        hasMoreDynamicValuesForTrial,
        dynamicListsValues: {},
    };

    /**
     * generates the values for this columns if dynamic lists are used
     * it uses values from each list sequentially. If one list is shorter than the others, it will use empty values.
     * @param {string} columnValue
     * @param {{listName: string, functionName: string|null, functionArgs: string|null, replaceValue}}[] listMatches
     * @returns {string[]|null} array of values for the column, or null if no dynamic list
     */
    function trialGeneratorListParallel(columnValue, listMatches) {
        if (!listMatches[0]) {
            return [columnValue];
        }

        // get the values for each list
        const listValues = listMatches.map((listMatch) => getComputedListValues(listMatch));

        // get the shortest list length
        const listLength = Math.max(...listValues.map((list) => list.length));

        // replace the replaceValue with the list value (using the shortest list length)
        const expandedColumnValues = [];
        for (let i = 0; i < listLength; i++) {
            let columnValueExpanded = columnValue;
            listValues.forEach((listValue, index) => {
                // empty value if the list is shorter than the longest list
                const value = listValue[i] || "";
                columnValueExpanded = columnValueExpanded.replace(listMatches[index].replaceValue, value);
            });
            expandedColumnValues.push(columnValueExpanded);
        }

        return expandedColumnValues;
    }

    function trialGeneratorListNested(columnValue, listMatches, listValues) {
        if (!listMatches[0]) {
            return [columnValue];
        }

        const expandedColumnValues = [];

        const listLength = listValues[listMatches[0].listName].length;

        for (let i = 0; i < listLength; i++) {

            let columnValueExpanded = columnValue;
            listMatches.forEach(listMatch => {
                const listValue = listValues[listMatch.listName][i]
                columnValueExpanded = columnValueExpanded.replace(listMatch.replaceValue, listValue)
            });
            expandedColumnValues.push(columnValueExpanded);
        }

        return expandedColumnValues;
    }

    /**
     * get the values for the dynamic list from the window.userVars object
     * @uses window.userVars
     * @param {string} listName
     * @returns {string[]|null} array of values for the column
     * 
     */
    function getDynamicListValues(listName) {
        const list = window.userVars[listName];

        if (!list) {
            return null;
        }

        // check if it's an array
        if (!Array.isArray(list)) {
            return null;
        }

        return [...list];
    }

    /**
     * get the values for the static list from the window.trialData object
     * @uses window.trialData.stimLists
     * @param {string} listName
     * @returns {string[]|undefined} array of values for the column stim
     *
     */
    function getStaticListValues(listName) {
        if (!window.trialData || !window.trialData.stimLists) {
            return undefined;
        }

        const list = window.trialData.stimLists[listName];

        // if is not an array, return undefined
        if (!Array.isArray(list)) {
            return undefined;
        }

        const columnName = 'stim';

        // the list is empty or the column stim doesn't exist
        if (!list[0] || !list[0][columnName]) {
            return undefined;
        }

        let listValues = list.map((data) => data[columnName]);

        // flatten if all values are a single value array
        if (listValues.every((value) => Array.isArray(value) && value.length === 1)) {
            listValues = listValues.map((value) => value[0]);
        }

        return listValues;
    }

    // internal functions

    /**
     * get the values for the dynamic list from the window.userVars object and apply the function if it exists
     * @uses window.userVars
     * @uses window.dynamicListsFunctions
     * @param {{listName: string, functionName: string, functionArgs: string[]}} listCall 
     * @returns {string[]} array of values for the column
     */
    function getComputedListValues(listCall) {
        const listName = listCall.listName;
        const list = getDynamicListValues(listName) || [];

        let listValues = list;
        const listFunction = listCall.functionName;

        // apply function
        if (window.dynamicListsFunctions[listFunction]) {
            listValues = window.dynamicListsFunctions[listFunction].apply(list, [list, listCall.functionArgs]);
        }

        return listValues;
    }

    /**
     * generate an array of values for the column replacing search with values from the list
     * 
     * @param {string} columnValue 
     * @param {string[]} listValues 
     * @param {string} search 
     * @returns {string[]}
     */
    function expandDynamicListValues(columnValue, listValues, search) {
        const columnValues = [];
        listValues.forEach(function (listValue) {
            columnValues.push(columnValue.replace(search, listValue));
        });

        return columnValues;
    }

    /**
     * expands a trial if it has dynamic lists
     * it stores the values in window.dynamicListsValues
     * 
     * @uses window.dynamicListsValues
     * @param {object} trialDynamicLists
     * @param {number} trialRow
     * @param {object} dynamicListsValues
     * @returns {boolean} true if the trial was expanded
     */
    function expandTrial(trialDynamicLists, trialRow, dynamicListsValues) {
        if (trialDynamicLists[trialRow]) {
            if (!dynamicListsValues[trialRow]) {
                dynamicListsValues[trialRow] = {};

                const listOptions = getListOptions(trialRow);
                const hasNestedOptionEnabled = listOptions.type === 'nested';

                // generate values for all dynamic lists
                if (!hasNestedOptionEnabled) {
                    for (const column in trialDynamicLists[trialRow]) {
                        const listMatch = trialDynamicLists[trialRow][column];
                        const columnValue = columnForTrial(column, trialRow);
                        dynamicListsValues[trialRow][column] = trialGeneratorListParallel(columnValue, listMatch);
                    }
                } else {
                    // combine all values for all columns in dynamicListsValues[trialRow]
                    const listsCallsForTrial = getListsForTrialRow(trialDynamicLists[trialRow]);
                    const nestedValues = combineValues(listsCallsForTrial, listOptions.lists, trialDynamicLists[trialRow]);
                    for (const column in trialDynamicLists[trialRow]) {
                        const listMatches = trialDynamicLists[trialRow][column];
                        const columnValue = columnForTrial(column, trialRow);
                        dynamicListsValues[trialRow][column] = trialGeneratorListNested(columnValue, listMatches, nestedValues)
                    }

                    // dynamicListsValues[trialRow] = combinedValues;
                }

                return true;
            }
        }

        return false;
    }

    function combineValues(listsCallsForTrial, nestedLists) {
        // get all combinations of values for all keys
        // example for 2 keys: {a: [1, 2], b: [3, 4]} => {a: [1, 1, 2, 2], b: [3, 4, 3, 4]}
        // don't call external functions
        const trialLists = listsCallsForTrial.map(listCall => listCall.listName);
        const keys = nestedLists ? nestedLists : trialLists;
        const parallelLists = [];
        trialLists.forEach(
            list => {
                if (!keys.includes(list)) {
                    parallelLists.push(list);
                }
            }
        )

        // sort listsCallsForTrial by keys
        listsCallsForTrial.sort((a, b) => {
            if (keys.indexOf(a.listName) < keys.indexOf(b.listName)) {
                return -1;
            } else if (keys.indexOf(a.listName) > keys.indexOf(b.listName)) {
                return 1;
            } else {
                return 0;
            }
        });

        const nestedValues = listsCallsForTrial
            .filter(listCall => keys.includes(listCall.listName))
            .map((listMatch) => [].concat.apply([], getComputedListValues(listMatch)));

        const parallelKeys = listsCallsForTrial.filter(listCall => parallelLists.includes(listCall.listName))
        //object with listName as key and listValues as value
        const parallelValues = {};
        parallelKeys.forEach(listCall => {
            parallelValues[listCall.listName] = getComputedListValues(listCall);
        });

        const combinations = nestedValues.reduce((a, b) =>
            a.map(
                x => b.map(y => x.concat([y]))
            ).reduce(
                (a, b) => a.concat(b), []
            ), [[]]);
        const combinedValues = combinations.map((combination) => {
            const combinedValue = {};
            combination.forEach((value, index) => {
                combinedValue[keys[index]] = value;
            });
            return combinedValue;
        });

        // convert the array of objects to an object of arrays
        const combinedValuesObject = {};
        combinedValues.forEach((combinedValue, valueIndex) => {
            for (const key in combinedValue) {
                if (!combinedValuesObject[key]) {
                    combinedValuesObject[key] = [];
                }
                combinedValuesObject[key].push(combinedValue[key]);
            }

            parallelLists.forEach((parallelList) => {
                if (!combinedValuesObject[parallelList]) {
                    combinedValuesObject[parallelList] = [];
                }

                let parallelValue = '';
                if (parallelValues[parallelList][valueIndex]) {
                    parallelValue = parallelValues[parallelList][valueIndex];
                }
                combinedValuesObject[parallelList].push(parallelValue);
            });
        });

        return combinedValuesObject;

    }

    function getListsForTrialRow(trialDynamicList) {
        const resultLists = [];

        for (const [column, columnLists] of Object.entries(trialDynamicList)) {
            for (const listCallIndex in columnLists) {
                resultLists.push(columnLists[listCallIndex]);
            }
        }

        return resultLists;
    }

    /**
     * get the value for given column for the dynamic list from the window.dynamicListsValues object
     * it also stores the value in window.actualValues
     * it also removes the value from the window.dynamicListsValues object
     * 
     * @uses window.dynamicListsValues
     * @uses window.trialRow
     * @uses window.actualValues
     * @uses window.responses
     * @param {string} expandingColumn
     * @returns {string|undefined} value for the column
     */
    function getDynamicValue(expandingColumn) {
        if (!window.dynamicLists.dynamicListsValues[window.trialRow]) {
            return undefined;
        }

        const values = window.dynamicLists.dynamicListsValues[window.trialRow][expandingColumn];
        if (!values || !values.length) {
            // if values for all columns are empty then unset the dynamic list values so if we have logic that returns to this trialRow we regenerate the values
            if (Object.keys(window.dynamicLists.dynamicListsValues[window.trialRow]).every((column) => !window.dynamicLists.dynamicListsValues[window.trialRow][column].length)) {
                delete window.dynamicLists.dynamicListsValues[window.trialRow];
            }

            return undefined;
        }
        const actualValue = values.shift();
        const actualResultColumn = `${expandingColumn}_actual`;
        if (!window.actualValues[actualResultColumn]) {
            window.actualValues[actualResultColumn] = [actualResultColumn];
        }
        const responsesCount = Math.max(window.responses.length, window.actualValues[actualResultColumn].length);
        window.actualValues[actualResultColumn][responsesCount] = actualValue;

        return actualValue;
    }


    /**
     * Check if there are more dynamic values for the trial
     * 
     * @uses window.dynamicListsValues
     * @param {number} trialRow
     * @returns {boolean} true if there are more dynamic values for the trial
     */
    function hasMoreDynamicValuesForTrial(trialRow) {
        if (!window.dynamicLists.dynamicListsValues[trialRow]) {
            return false;
        }

        const trialRowData = window.dynamicLists.dynamicListsValues[trialRow];

        for (const column in trialRowData) {
            const values = trialRowData[column];
            if (values && values.length) {
                return true;
            }
        }

        return false;
    }

    /**
     * Parses the listOptions column (if any) and returns an object
     * containing the type
     * and lists involved in the options if the type is nested
     * 
     * @param {number} trialRow
     * @returns {{type: string, lists:string[]|undefined}}
     */
    function getListOptions(trialRow) {
        let options = { type: 'parallel' };

        const listOptionsRegex = /(?<listType>(parallel)|(nested))(\((?<listParams>\w+(,\s*\w+)*)\))?/
        const listOptions = window.columnForTrial('listOptions', trialRow);

        if (!listOptions) {
            return options;
        }

        const matches = listOptions.match(listOptionsRegex);

        if (matches !== null) {
            options.type = matches.groups.listType;

            if (matches.groups.listParams) {
                const foundLists = matches.groups.listParams.split(',');
                const parsedLists = foundLists.map(list => {
                    return list.trim();
                })
                options.lists = parsedLists;
            }
        }

        return options;
    }

    /**
     * initialize dynamic lists functions
     * @uses window.dynamicListsFunctions
     */
    function initFunctions() {
        window.dynamicListsFunctions = {
            random: function (list) {
                shuffle(list);
                return list;
            },
            sample: function (list, size) {
                shuffle(list);
                return list.slice(0, size);
            },
        };
    }
}